<template>
  <div class="mx-4">
    <!-- Header with Actions -->
    <div class="flex justify-between items-center mb-6 w-full">
      <div class="text-xl font-bold text-gray-900 dark:text-gray-100">话题检测</div>
      <div class="flex gap-4">
        <div>
          <LoadingBtn :text="'检测新话题'" :loading-text="'检测中...'" :is-loading="isDetecting" @click="detectTopics" />
        </div>
        <div>
          <LoadingBtn :text="'爬取话题'" :loading-text="'爬取中...'" :is-loading="isCrawling" @click="crawlTopics"
            button-class="secondary-btn" />
        </div>
        <div>
          <LoadingBtn :text="'导入话题'" :loading-text="'导入中...'" :is-loading="isImporting" @click="importTopics"
            button-class="secondary-btn" />
        </div>
        <div>
          <LoadingBtn :text="'生成模型答案'" :loading-text="'准备中...'" :is-loading="isGenerating"
            @click="startModelAnswerGeneration" button-class="primary-btn" />
        </div>
        <div>
          <LoadingBtn :text="'生成所有音频'" :loading-text="'生成中...'" :is-loading="isGeneratingAudio"
            @click="startAudioGeneration" button-class="secondary-btn" />
        </div>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" v-if="detectionSummary">
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-500 dark:text-gray-400">新话题</div>
        <div class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
          {{ detectionSummary.newTopicsCount || 0 }}
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-500 dark:text-gray-400">更新话题</div>
        <div class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
          {{ detectionSummary.updatedTopicsCount || 0 }}
        </div>
      </div>
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-500 dark:text-gray-400">可见性漂移</div>
        <div class="text-2xl font-bold" :class="visibilityDriftCount > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'">
          {{ visibilityDriftCount }}
        </div>

      </div>
    </div>

    <!-- Topics Table -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">检测结果</h3>
      </div>

      <div class="overflow-x-auto">
        <LoadingSpinner v-if="isLoading" text="加载中..." />

        <table v-else-if="detectionSummary?.topics?.length > 0" class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                话题名称
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Part
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                状态
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                新问题数
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="topic in detectionSummary.topics" :key="topic.topicId" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                {{ topic.topicName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">Part {{ topic.partId }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex flex-wrap gap-1">
                  <span v-if="topic.isNewTopic"
                    class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    新话题
                  </span>
                  <span v-if="topic.hasNewQuestions"
                    class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                    有新问题
                  </span>
                  <span v-if="topic.hasVisibilityDrift || isVisibilityDrift(topic.topicId)"
                    class="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
                    title="DB 中 is_visible 为 false，与爬取预期不一致">
                    可见性异常
                  </span>
                  <span v-if="!topic.isNewTopic && !topic.hasNewQuestions && !(topic.hasVisibilityDrift || isVisibilityDrift(topic.topicId))"
                    class="text-xs text-gray-400 dark:text-gray-500">—</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                {{ topic.newQuestions.length }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                <button @click="showQuestions(topic)" class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300">
                  查看问题
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">暂无检测结果</div>
      </div>
    </div>

    <!-- Questions Modal -->
    <Modal :show="showQuestionsModal" @close="closeQuestionsModal">
      <template #header>
        <div class="text-lg font-medium">
          {{ selectedTopic?.topicName }}
        </div>
      </template>

      <template #body>
        <div class="space-y-4">
          <div v-for="(question, index) in selectedTopic?.newQuestions" :key="question.questionId"
            class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div class="font-medium text-gray-900 dark:text-gray-100">问题 {{ index + 1 }}</div>
            <div class="mt-2 text-gray-600 dark:text-gray-300">{{ question.question }}</div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800"
          @click="closeQuestionsModal">
          关闭
        </button>
      </template>
    </Modal>

    <!-- Model Answer Generation Progress -->
    <Modal :show="showGenerationModal" @close="closeGenerationModal" modalSize="sm:my-8 sm:w-full sm:max-w-md sm:p-6">
      <template #header>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
            模型答案生成
          </h3>
        </div>
      </template>
      <template #body>
        <div class="mt-2">
          <ModelAnswerProgress @complete="handleGenerationComplete" @error="handleGenerationError" />
        </div>
      </template>
      <template #footer>
        <button @click="closeGenerationModal"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm">
          关闭
        </button>
      </template>
    </Modal>

    <!-- Success Notification Modal -->
    <Modal :show="showCompletionNotification" @close="showCompletionNotification = false"
      modalSize="sm:my-8 sm:w-full sm:max-w-md sm:p-6">
      <template #header>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
            模型答案生成完成
          </h3>
        </div>
      </template>
      <template #body>
        <div class="mt-2 space-y-4">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            成功生成 {{ generationResult.processed_questions }} 个模型答案
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-300">处理话题总数：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ generationResult.processed_topics }} /
                {{ generationResult.total_topics }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-300">处理问题总数：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ generationResult.processed_questions }} /
                {{ generationResult.total_questions }}</span>
            </div>
            <div v-if="
              generationResult.failed_questions &&
              generationResult.failed_questions.length > 0
            " class="flex justify-between text-sm">
              <span class="text-red-600 dark:text-red-400">失败问题数：</span>
              <span class="font-medium text-red-600 dark:text-red-400">{{
                generationResult.failed_questions.length
                }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button @click="showCompletionNotification = false"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-500 focus:outline-none sm:w-auto sm:text-sm">
          确认
        </button>
      </template>
    </Modal>

    <!-- Audio Generation Progress Modal -->
    <Modal :show="showAudioGenerationModal" @close="closeAudioGenerationModal"
      modalSize="sm:my-8 sm:w-full sm:max-w-md sm:p-6">
      <template #header>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
            音频生成进行中
          </h3>
        </div>
      </template>
      <template #body>
        <div class="mt-2">
          <div class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {{ audioGenerationMessage }}
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div class="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
              :style="{ width: audioGenerationProgress + '%' }"></div>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            {{ audioGenerationProgress }}%
          </div>
        </div>
      </template>
      <template #footer>
        <button @click="closeAudioGenerationModal"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm">
          关闭
        </button>
      </template>
    </Modal>

    <!-- Topic Import Progress Modal (socket async, reuse audio/model-answer style) -->
    <Modal :show="showImportModal" @close="closeImportModal" modalSize="sm:my-8 sm:w-full sm:max-w-md sm:p-6">
      <template #header>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-500 mr-2" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
            话题导入进行中
          </h3>
        </div>
      </template>
      <template #body>
        <div class="mt-2">
          <div class="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {{ importMessage }}
          </div>
          <div v-if="importTotal > 0" class="text-xs text-gray-400 dark:text-gray-500 mb-3">
            {{ importCurrent }} / {{ importTotal }}
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div class="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
              :style="{ width: importProgress + '%' }"></div>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            {{ importProgress }}%
          </div>
          <div v-if="importError" class="mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-2 rounded">{{ importError }}</div>
        </div>
      </template>
      <template #footer>
        <button @click="closeImportModal"
          class="mt-3 w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 text-base font-medium focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
          :class="isImporting ? 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600' : 'bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-500 border border-transparent'">
          {{ isImporting ? '取消导入' : '关闭' }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import LoadingBtn from "@/components/ui/LoadingBtn.vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import Modal from "@/components/ui/Modal.vue";
import type { DetectionSummary, DetectedTopic, VisibilityDrift } from "@/types/topic/topicDetector";
import {
  detectTopicsAPI,
  crawlTopicsAPI,
  importTopicsAPI,
} from "@/api/speaking/topicDetector";
import toast from "@/utils/toaster";
import ModelAnswerProgress from "@/components/speaking/ModelAnswerProgress.vue";
import { Manager } from "socket.io-client";
import { useToken } from "@/hooks/useToken";

const isLoading = ref(false);
const isDetecting = ref(false);
const isCrawling = ref(false);
const isImporting = ref(false);
const isGenerating = ref(false);
const isGeneratingAudio = ref(false);
const detectionSummary = ref<DetectionSummary>({
  newTopicsCount: 0,
  updatedTopicsCount: 0,
  topics: [],
  visibilityDrift: [],
  visibilityDriftCount: 0,
});

// Visibility drift helpers
const visibilityDriftCount = computed(() => {
  const v = detectionSummary.value as any;
  return v.visibilityDriftCount ?? v.visibility_drift_count ?? v.visibilityDrift?.length ?? v.visibility_drift?.length ?? 0;
});
const visibilityDriftIds = computed(() => {
  const v = detectionSummary.value as any;
  const list: VisibilityDrift[] = v.visibilityDrift ?? v.visibility_drift ?? [];
  return new Set(list.map((d) => String(d.topicId ?? (d as any).topic_id ?? (d as any).topicId)));
});
const isVisibilityDrift = (topicId: string) => visibilityDriftIds.value.has(String(topicId));

// Topic import (socket async) state
const showImportModal = ref(false);
const importProgress = ref(0);
const importMessage = ref("准备开始...");
const importCurrent = ref(0);
const importTotal = ref(0);
const importError = ref("");
let importSocket: any = null;
const showQuestionsModal = ref(false);
const selectedTopic = ref<DetectedTopic | null>(null);
const crawledTopics = ref<any[]>([]);
const showGenerationModal = ref(false);
const showCompletionNotification = ref(false);
const showAudioGenerationModal = ref(false);
const audioGenerationProgress = ref(0);
const audioGenerationMessage = ref("准备开始...");
const generationResult = ref<any>({
  total_topics: 0,
  processed_topics: 0,
  total_questions: 0,
  processed_questions: 0,
  failed_questions: [],
});

const detectTopics = async () => {
  try {
    isDetecting.value = true;
    isLoading.value = true;
    const response = await detectTopicsAPI();

    if (response.data) {
      const raw: any = response.data;
      const driftRaw: any[] = raw.visibilityDrift ?? raw.visibility_drift ?? [];
      const mappedDrift: VisibilityDrift[] = driftRaw.map((d: any) => ({
        topicId: String(d.topicId ?? d.topic_id ?? ""),
        topicName: d.topicName ?? d.topic_name ?? "",
        expected: d.expected ?? true,
        actual: d.actual ?? false,
      }));
      const driftIdSet = new Set(mappedDrift.map((d) => String(d.topicId)));
      const mappedTopics: DetectedTopic[] = (raw.topics || []).map((topic: any) => ({
        topicId: String(topic.topicId ?? topic.topic_id ?? ""),
        topicName: topic.topicName ?? topic.topic_name ?? "",
        partId: topic.partId ?? topic.part_id ?? 0,
        categoryId: String(topic.categoryId ?? topic.category_id ?? ""),
        orderId: topic.orderId ?? topic.order_id ?? 0,
        hasNewQuestions: topic.hasNewQuestions ?? topic.has_new_questions ?? false,
        isNewTopic: topic.isNewTopic ?? topic.is_new_topic ?? false,
        newQuestions: (topic.newQuestions ?? topic.new_questions ?? []).map((q: any) => ({
          questionId: String(q.questionId ?? q.question_id ?? ""),
          question: q.question ?? "",
        })),
        hasVisibilityDrift: topic.hasVisibilityDrift ?? topic.has_visibility_drift ?? driftIdSet.has(String(topic.topicId ?? topic.topic_id ?? "")),
      }));
      // Merge: ensure drift-only topics appear even if backend omitted them (fallback)
      const topicMap = new Map<string, DetectedTopic>(mappedTopics.map((t) => [String(t.topicId), t]));
      for (const d of mappedDrift) {
        const id = String(d.topicId);
        if (topicMap.has(id)) {
          topicMap.get(id)!.hasVisibilityDrift = true;
        } else {
          topicMap.set(id, {
            topicId: id,
            topicName: d.topicName,
            partId: 0,
            categoryId: "",
            orderId: 0,
            hasNewQuestions: false,
            isNewTopic: false,
            newQuestions: [],
            hasVisibilityDrift: true,
          });
        }
      }
      const finalTopics = Array.from(topicMap.values());
      detectionSummary.value = {
        newTopicsCount: raw.newTopicsCount ?? raw.new_topics_count ?? 0,
        updatedTopicsCount: raw.updatedTopicsCount ?? raw.updated_topics_count ?? 0,
        topics: finalTopics,
        visibilityDrift: mappedDrift,
        visibilityDriftCount: raw.visibilityDriftCount ?? raw.visibility_drift_count ?? mappedDrift.length,
      } as DetectionSummary;

      if (finalTopics.length > 0 || mappedDrift.length > 0) {
        const driftMsg = mappedDrift.length ? `，${mappedDrift.length} 个可见性漂移` : "";
        toast.success(
          `检测到 ${detectionSummary.value.newTopicsCount} 个新话题和 ${detectionSummary.value.updatedTopicsCount} 个更新话题${driftMsg}`
        );
      } else {
        toast.info("未检测到新话题");
      }
    }
  } catch (error) {
    console.error("Detection error:", error);
    toast.error("检测失败");
  } finally {
    isDetecting.value = false;
    isLoading.value = false;
  }
};

const crawlTopics = async () => {
  try {
    isCrawling.value = true;
    isLoading.value = true;
    const response = await crawlTopicsAPI();
    console.log("Crawl response:", response);

    if (response.status === 200 && response.data) {
      // Combine topics from part 0 and part 1
      const part0Topics = response.data[0] || [];
      const part1Topics = response.data[1] || [];
      crawledTopics.value = [...part0Topics, ...part1Topics];

      toast.success(`爬取完成，共获取 ${crawledTopics.value.length} 个话题`);
    } else {
      toast.error("爬取失败");
    }
  } catch (error) {
    console.error("Crawl error:", error);
    toast.error("爬取失败");
  } finally {
    isCrawling.value = false;
    isLoading.value = false;
  }
};

const importTopics = async () => {
  if (isImporting.value) {
    toast.warning("导入进行中，请勿重复点击");
    return;
  }
  if (!crawledTopics.value.length && !detectionSummary.value.topics.length) {
    toast.warning("请先检测或爬取话题后再导入");
    return;
  }
  // Use socket async import with progress; fallback to HTTP if socket unavailable
  isImporting.value = true;
  showImportModal.value = true;
  importProgress.value = 0;
  importMessage.value = "正在启动导入...";
  importCurrent.value = 0;
  importTotal.value = 0;
  importError.value = "";
  setupImportSocket();
};

const setupImportSocket = () => {
  const token = getToken();
  const manager = new Manager(import.meta.env.VITE_SOCKET_URL, {
    query: { token },
    transports: ["websocket"],
  });
  importSocket = manager.socket("/");

  const handleProgress = (data: any) => {
    const current = data.current ?? data.processed_topics ?? 0;
    const total = data.total ?? data.total_topics ?? 0;
    const progress = data.progress ?? (total ? Math.round((current / total) * 100) : 0);
    importCurrent.value = current;
    importTotal.value = total;
    importProgress.value = progress;
    importMessage.value = data.message ?? data.current_topic ?? `正在导入 ${current}/${total}`;
  };
  const handleComplete = async (data: any) => {
    importProgress.value = 100;
    importMessage.value = "导入完成！";
    const result = data.result ?? data;
    const topics = result.topics ?? result.total_topics ?? 0;
    const questions = result.questions ?? result.total_questions ?? 0;
    toast.success(`导入成功 (${topics} 个话题, ${questions} 个问题)`);
    isImporting.value = false;
    setTimeout(() => {
      showImportModal.value = false;
    }, 1200);
    // cleanup socket after a delay, keep for potential late events
    setTimeout(() => {
      if (importSocket) {
        try { importSocket.disconnect(); } catch {} 
        importSocket = null;
      }
    }, 1500);
    await detectTopics();
  };
  const handleError = (data: any) => {
    const msg = data?.message ?? data?.detail ?? "导入失败";
    // 409 lock case
    if (String(msg).includes("already in progress") || String(msg).includes("进行中")) {
      importError.value = "导入已在进行中，请稍后重试";
      toast.warning("导入已在进行中");
    } else {
      importError.value = msg;
      toast.error(msg);
    }
    isImporting.value = false;
  };
  const handleCancelled = (data: any) => {
    importError.value = "已取消";
    toast.info(data?.message ?? "导入已取消");
    isImporting.value = false;
    showImportModal.value = false;
    if (importSocket) { try { importSocket.disconnect(); } catch {} importSocket = null; }
  };

  importSocket.on("topic:import:progress", handleProgress);
  importSocket.on("topic:import:complete", handleComplete);
  importSocket.on("topic:import:error", handleError);
  importSocket.on("topic:import:cancelled", handleCancelled);
  importSocket.on("topic:import:started", () => {
    importMessage.value = "导入已开始...";
  });

  const doEmit = () => {
    try { importSocket.emit("topic:import:start"); } catch {}
  };
  // socket.io may need a tick after connect
  if (importSocket.connected) {
    doEmit();
  } else {
    importSocket.on("connect", doEmit);
    // fallback: if not connected within 3s, try HTTP sync import
    setTimeout(async () => {
      if (importProgress.value === 0 && importCurrent.value === 0) {
        // check if socket connected
        const connected = importSocket?.connected;
        if (!connected) {
          toast.info("Socket 未连接，回退到 HTTP 导入");
          try {
            // cleanup socket
            try { importSocket.disconnect(); } catch {}
            importSocket = null;
            const response: any = await importTopicsAPI();
            if (response.status === 200 && response.data) {
              const result: any = response.data;
              importProgress.value = 100;
              importMessage.value = "导入完成！";
              toast.success(`导入成功 (${result.topics} 个话题, ${result.questions} 个问题)`);
              await detectTopics();
            } else {
              handleError({ message: "导入失败" });
            }
          } catch (error: any) {
            const detail = error?.response?.data?.detail ?? error?.message ?? "导入失败";
            if (String(detail).includes("already in progress") || error?.response?.status === 409) {
              handleError({ message: "Import already in progress" });
            } else {
              handleError({ message: detail });
            }
          } finally {
            isImporting.value = false;
            setTimeout(() => { showImportModal.value = false; }, 1200);
          }
        }
      }
    }, 3500);
  }
  importSocket.on("connect_error", (err: any) => {
    console.warn("import socket connect_error", err);
  });
};

const closeImportModal = () => {
  if (isImporting.value) {
    if (!confirm("确定要中断导入吗？")) return;
    if (importSocket) {
      try { importSocket.emit("topic:import:stop"); } catch {}
    }
    // don't close immediately; wait for cancelled event, but allow force close after timeout
    setTimeout(() => {
      if (isImporting.value) {
        isImporting.value = false;
        showImportModal.value = false;
        if (importSocket) { try { importSocket.disconnect(); } catch {} importSocket = null; }
      }
    }, 1200);
  } else {
    showImportModal.value = false;
    if (importSocket) { try { importSocket.disconnect(); } catch {} importSocket = null; }
    importError.value = "";
  }
};

const showQuestions = (topic: DetectedTopic) => {
  selectedTopic.value = topic;
  showQuestionsModal.value = true;
};

const closeQuestionsModal = () => {
  showQuestionsModal.value = false;
  selectedTopic.value = null;
};

const startModelAnswerGeneration = () => {
  isGenerating.value = true;
  showGenerationModal.value = true;
};

const closeGenerationModal = () => {
  if (isGenerating.value) {
    if (!confirm("确定要中断生成过程吗？")) {
      return;
    }
  }
  showGenerationModal.value = false;
  isGenerating.value = false;
};

const handleGenerationComplete = (result: any) => {
  isGenerating.value = false;
  // Store the result for the completion notification
  generationResult.value = result;

  // Close generation modal and show completion notification
  setTimeout(() => {
    showGenerationModal.value = false;
    showCompletionNotification.value = true;
  }, 500);
};

const handleGenerationError = (error: any) => {
  isGenerating.value = false;
};

// Shared token + socket refs
const { getToken } = useToken();
let audioSocket: any = null;

const startAudioGeneration = () => {
  isGeneratingAudio.value = true;
  showAudioGenerationModal.value = true;
  audioGenerationProgress.value = 0;
  audioGenerationMessage.value = "正在启动音频生成...";

  setupAudioSocket();
};

const setupAudioSocket = () => {
  const token = getToken();
  const manager = new Manager(import.meta.env.VITE_SOCKET_URL, {
    query: { token },
    transports: ["websocket"],
  });
  audioSocket = manager.socket("/");

  audioSocket.on("audio_generation_progress", (data: any) => {
    audioGenerationProgress.value = data.progress || 0;
    audioGenerationMessage.value = data.message || "正在生成音频...";
  });

  audioSocket.on("audio_generation_complete", (data: any) => {
    audioGenerationProgress.value = 100;
    audioGenerationMessage.value = "音频生成完成！";
    isGeneratingAudio.value = false;
    toast.success("所有音频生成完成");

    setTimeout(() => {
      showAudioGenerationModal.value = false;
    }, 2000);
  });

  audioSocket.on("audio_generation_cancelled", (data: any) => {
    audioGenerationProgress.value = 0;
    audioGenerationMessage.value = "音频生成已取消";
    isGeneratingAudio.value = false;
    toast.info(data.message || "音频生成已取消");
    showAudioGenerationModal.value = false;
  });

  audioSocket.on("error", (error: any) => {
    isGeneratingAudio.value = false;
    toast.error(error.message || "音频生成失败");
    showAudioGenerationModal.value = false;
  });

  audioSocket.emit("start_audio_generation");
};

const closeAudioGenerationModal = () => {
  if (isGeneratingAudio.value) {
    if (!confirm("确定要中断音频生成过程吗？")) {
      return;
    }
    // Send stop signal to backend
    if (audioSocket) {
      audioSocket.emit("stop_audio_generation");
    }
  } else {
    showAudioGenerationModal.value = false;
    if (audioSocket) {
      audioSocket.disconnect();
    }
  }
};

// Initial detection on component mount
onMounted(() => {
  detectTopics();
});

onUnmounted(() => {
  if (audioSocket) {
    try { audioSocket.disconnect(); } catch {}
  }
  if (importSocket) {
    try { importSocket.disconnect(); } catch {}
    importSocket = null;
  }
});
</script>
